import styled from '@emotion/styled'
import { ContextKey } from '@pubstack/common/src/contextKey'
import { FunctionComponent, useEffect, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { Colors } from '~/assets/style/colors'
import { useScopePermissions } from '~/auth/useScopePermissions'
import Button from '~/components/Button'
import { Tooltip } from '~/components/Tooltip'
import Chip from '~/components/Chip'
import { IllustrationMessage } from '~/components/IllustrationMessage'
import { Input } from '~/components/Input'
import { Pagination } from '~/components/Pagination'
import Table, { handleTableSearchAndSort, onColumnSort, TableColumns } from '~/components/table/Table'
import TableCell from '~/components/table/TableCell'
import TableRow from '~/components/table/TableRow'
import { useClientPagination } from '~/hooks/useClientPagination'

type PureAdmContextKeysPageProps = {
  contextKeys: ContextKey[]
  isLoading: boolean
  scopeId?: string
  onCreateContextKey: () => void
  onUpdateContextKey: (contextKey: ContextKey) => void
  breadcrumbs: React.ReactNode
}

const ContextKeysWrapper = styled.div`
  padding: 0px;
`

const ActionsTableCell = styled(TableCell)`
  width: 0px; /** necessary to get the last cell to shrink down to hug action buttons */
`
const ChipTableCell = styled(TableCell)`
  width: 75%;
`
const ButtonActions = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
`

const FilterAction = styled.form`
  margin: 12px 0px;
  display: flex;
  width: 100%;
  justify-content: space-between;
`

const ValueChipsWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`

export const PureAdmContextKeysPage: FunctionComponent<PureAdmContextKeysPageProps> = ({ contextKeys, isLoading, onCreateContextKey, onUpdateContextKey }) => {
  const { canCreate, disabledReason } = useScopePermissions()
  const MAX_DISPLAYED_VALUE = 10
  const [columns, setColumns] = useState<TableColumns<ContextKey>>([
    {
      name: 'Name',
      isSortable: true,
      order: 'ascending',
      attributeSort: 'name',
    },
    {
      name: 'Values',
      isSortable: false,
    },
    {
      name: 'Action',
      isSortable: false,
    },
  ])

  const { control } = useForm<{ search: string }>({
    defaultValues: {
      search: '',
    },
  })

  const search = useWatch({ control, name: 'search' })

  const sortAndGroupContextKeys = (ContextKeys: ContextKey[]) => handleTableSearchAndSort(columns, ContextKeys, search, ['name'])
  const displayedContextKeys = [...sortAndGroupContextKeys(contextKeys)]
  /* Paged in the browser: the endpoint answers with the whole list, and a scope with a few hundred keys
     was one unbroken scroll. Narrowing the search clamps the page rather than stranding the reader on an
     empty one — see `useClientPagination`. */
  const { pageItems, paginationProps } = useClientPagination(displayedContextKeys)
  /* A key is what every context conditions on, so making one is an owner's. The button stays on screen
     saying why — seeing that the capability exists is half of knowing to ask an owner for it. */
  const newContextKeyButton = (
    <Tooltip title={disabledReason} disabled={canCreate}>
      <Button
        variant={'primary'}
        disabled={!canCreate}
        onClick={() => {
          onCreateContextKey()
        }}
        trackingLabel={'adm-context-keys-new'}
      >
        New context key
      </Button>
    </Tooltip>
  )

  useEffect(() => {
    sortAndGroupContextKeys(contextKeys)
  }, [])

  return (
    <ContextKeysWrapper>
      {isLoading || contextKeys?.length > 0 ? (
        <>
          <FilterAction>
            <Input name={'search'} type={'text'} iconLeft={'search'} labelIsPlaceholder label={'Search'} control={control} />
            {contextKeys?.length > 0 && newContextKeyButton}
          </FilterAction>
          <Table columns={columns} isLoading={isLoading} onClickHeading={(column) => onColumnSort(columns, column, setColumns)}>
            {pageItems.map((contextKey) => (
              <TableRow key={contextKey.name} clickable={{ onClick: () => onUpdateContextKey(contextKey), trackingLabel: 'adm-context-keys-edit' }}>
                <TableCell>{contextKey.name}</TableCell>
                <ChipTableCell>
                  <ValueChipsWrapper>
                    {contextKey.values.map(
                      (value, i) =>
                        value &&
                        i < MAX_DISPLAYED_VALUE && (
                          <Chip
                            iconLeft={value === contextKey.defaultValue ? { name: 'star' } : undefined}
                            key={i}
                            text={value}
                            color={value === contextKey.defaultValue ? Colors.Violet : Colors.Milka}
                          />
                        )
                    )}
                    {contextKey.values.length > MAX_DISPLAYED_VALUE && <span>+ {contextKey.values.length - MAX_DISPLAYED_VALUE} values</span>}
                  </ValueChipsWrapper>
                </ChipTableCell>
                <ActionsTableCell>
                  <ButtonActions>
                    <Button
                      iconName={'edit'}
                      variant={'tertiary'}
                      title={'Edit'}
                      onClick={() => {
                        onUpdateContextKey(contextKey)
                      }}
                      trackingLabel={'adm-context-keys-icon-edit'}
                    />
                  </ButtonActions>
                </ActionsTableCell>
              </TableRow>
            ))}
          </Table>
          <Pagination {...paginationProps} className={'mt-4'} />
        </>
      ) : (
        <IllustrationMessage iconName={'night_sky'} iconSize={'190px'} title={'There is no context key.'}>
          {newContextKeyButton}
        </IllustrationMessage>
      )}
    </ContextKeysWrapper>
  )
}
